import { z } from "zod";

// Saved preferences do not grant financial access or start synchronization.
export const apiConnectionAdvancedSchema = z.object({
  authMethod: z.enum(["bearer", "api_key"]).default("bearer"),
  apiKeyHeader: z.enum(["x-api-key", "api-key", "x-auth-token", "x-access-token"]).default("x-api-key"),
  credentialExpiresOn: z.iso.date().nullable().default(null),
  requestTimeoutSeconds: z.number().int().min(1).max(30).default(5),
  syncMode: z.enum(["manual", "scheduled"]).default("manual"),
  syncIntervalMinutes: z.number().int().min(5).max(1440).default(60),
  initialImportDate: z.iso.date().nullable().default(null),
  retryLimit: z.number().int().min(0).max(10).default(3),
  retryDelaySeconds: z.number().int().min(10).max(3600).default(60),
  failureThreshold: z.number().int().min(1).max(20).default(3),
  notificationEmails: z.array(z.string().trim().email().max(254)).max(10)
    .transform((values) => [...new Set(values.map((value) => value.toLowerCase()))]).default([]),
}).strict();
export type ApiConnectionAdvanced = z.infer<typeof apiConnectionAdvancedSchema>;
export const defaultApiConnectionAdvanced = (): ApiConnectionAdvanced => apiConnectionAdvancedSchema.parse({});

const dataId = z.string().regex(/^[a-z][a-z0-9_.-]{0,63}$/);
const choice = z.object({ id: dataId, label: z.string().trim().min(1).max(80) }).strict();
const choices = z.array(choice).max(10).refine((items) => new Set(items.map((item) => item.id)).size === items.length, "Duplicate option IDs.");
export const apiDataCapabilitySchema = z.object({
  id: dataId,
  schemaVersion: z.string().regex(/^[a-zA-Z0-9_.-]{1,32}$/),
  name: z.string().trim().min(1).max(80),
  description: z.string().max(240).default(""),
  // Directions are from the advertising system's point of view.
  canSend: z.boolean(),
  canReceive: z.boolean(),
  matchingKeys: choices.default([]),
  deliveryOptions: choices.default([]),
}).strict().refine((item) => item.canSend || item.canReceive, "A data type needs a supported direction.");
export const apiDataCatalogSchema = z.object({
  version: z.literal(1),
  items: z.array(apiDataCapabilitySchema).max(30)
    .refine((items) => new Set(items.map((item) => item.id)).size === items.length, "Duplicate data IDs."),
}).strict();
export type ApiDataCapability = z.infer<typeof apiDataCapabilitySchema>;
export type ApiDataCatalog = z.infer<typeof apiDataCatalogSchema>;
export const apiDataPermissionsSchema = z.array(z.object({
  capabilityId: dataId,
  schemaVersion: z.string().regex(/^[a-zA-Z0-9_.-]{1,32}$/),
  sendToPeer: z.boolean(),
  receiveFromPeer: z.boolean(),
  matchingKey: dataId.nullable(),
  deliveryOption: dataId.nullable(),
}).strict()).max(30).refine((items) => new Set(items.map((item) => item.capabilityId)).size === items.length, "Duplicate data permissions.");
export type ApiDataPermission = z.infer<typeof apiDataPermissionsSchema>[number];

/** Validate intent against discovered evidence; this does not grant runtime access. */
export function dataPermissionError(permissions: ApiDataPermission[], catalog: ApiDataCatalog | null): string | null {
  for (const permission of permissions) {
    const item = catalog?.items.find((candidate) => candidate.id === permission.capabilityId);
    if (!item || item.schemaVersion !== permission.schemaVersion) return "Discover this data type and version before configuring its permissions.";
    if ((permission.sendToPeer && !item.canReceive) || (permission.receiveFromPeer && !item.canSend)) return "The other system does not advertise that data direction.";
    if (permission.matchingKey && !item.matchingKeys.some((option) => option.id === permission.matchingKey)) return "Choose a discovered record-matching option.";
    if (permission.deliveryOption && !item.deliveryOptions.some((option) => option.id === permission.deliveryOption)) return "Choose a discovered delivery option.";
  }
  return null;
}

export type ConnectionEnvironment = "local" | "staging" | "production";
export interface ApiConnection {
  id: string;
  slug: string;
  name: string;
  environment: ConnectionEnvironment;
  baseUrl: string | null;
  enabled: boolean;
  version: number;
  hasOutboundToken: boolean;
  advanced: ApiConnectionAdvanced;
  dataCatalog: ApiDataCatalog | null;
  catalogDiscoveredAt: string | null;
  dataPermissions: ApiDataPermission[];
  lastTestAt: string | null;
  lastTestStatus: "passed" | "failed" | null;
  lastTestMessage: string | null;
  lastInboundAt: string | null;
}
export interface ApiConnectionsResponse {
  connections: ApiConnection[];
  environment: ConnectionEnvironment | null;
  canManage: boolean;
  canStoreOutboundCredentials: boolean;
  handshakePath: string;
  localDataCatalog: ApiDataCatalog;
}
export interface SaveApiConnection {
  expectedVersion: number;
  slug: string;
  name: string;
  baseUrl: string | null;
  enabled: boolean;
  outboundToken?: string;
  clearOutboundToken?: boolean;
  advanced?: ApiConnectionAdvanced;
  dataPermissions?: ApiDataPermission[];
}
